import { ReactElement } from "react";

import { gql } from "@apollo/client";
import { AppBar, Box, TextInfo, Typography } from "@portex-pro/ui-components";
import lowerCase from "lodash/lowerCase";
import { useTranslation } from "react-i18next";

import { QuoteRequest } from "../../../api/types/generated-types";
import LoadTypeChip from "../../../components/LoadTypeChip";
import ModeIcon from "../../../components/ModeIcon";
import { EM_DASH } from "../../../constants";
import { formatWeight } from "../../../utils/formatUnit";
import { getLtlTrailerColumnLabel } from "../pages/quotes/utils/getLtlTrailerColumnLabel";
import { getCityToCity } from "../pages/quotes/utils/getQuoteRequestDetails";
import ReviewSendQuoteRequest from "../pages/request-quote/pages/ftl/components/ReviewSendQuoteRequest";
import QuoteRequestHeaderActions from "./QuoteRequestHeaderActions";
import StateStatus from "./StateStatus";

const STOP = gql`
  fragment LtlQuoteRequestHeader_Stop on Stop {
    id
    position
    start
    end
    address {
      id
      city
      iana_timezone
      type
      province_code
      province_name
    }
  }
`;

const LTL_LOAD_SPEC = gql`
  fragment LtlQuoteRequestHeader_LtlLoadSpec on LtlLoadSpec {
    id
    max_temp
    min_temp
    trailer_type
    weight
    weight_unit
  }
`;

const QUOTE_REQUEST = gql`
  fragment LtlQuoteRequestHeader_QuoteRequest on QuoteRequest {
    id
    guid
    portex_id
    mode
    state
    note
    ltl_load_spec {
      ...LtlQuoteRequestHeader_LtlLoadSpec
    }
    stops {
      ...LtlQuoteRequestHeader_Stop
    }
    ...ReviewSendQuoteRequest_QuoteRequest
  }
  ${LTL_LOAD_SPEC}
  ${STOP}
  ${ReviewSendQuoteRequest.fragments.QuoteRequest}
`;

type LtlQuoteRequestHeaderProps = {
  quoteRequest: QuoteRequest;
  refetchQuoteRequest?: () => Promise<unknown>;
};

const LtlQuoteRequestHeader = ({ quoteRequest, refetchQuoteRequest }: LtlQuoteRequestHeaderProps): ReactElement => {
  const { t } = useTranslation(["common", "shipper"]);
  const headerTitle = getCityToCity(quoteRequest.stops);
  const portexId = quoteRequest.portex_id;
  const trailerType = getLtlTrailerColumnLabel(quoteRequest.ltl_load_spec ?? undefined);
  const totalWeight =
    quoteRequest.ltl_load_spec?.weight && quoteRequest.ltl_load_spec?.weight_unit
      ? formatWeight(quoteRequest.ltl_load_spec?.weight, lowerCase(quoteRequest.ltl_load_spec?.weight_unit))
      : EM_DASH;

  return (
    <AppBar>
      <Box
        py={1}
        px={2}
        display="flex"
        flexDirection="row"
        alignItems="center"
        bgcolor="background.paper"
        justifyContent="space-between"
      >
        <Box display={"flex"} alignItems={"center"}>
          <Box mt={"8px"} mr={"8px"}>
            <ModeIcon mode={quoteRequest.mode} large />
          </Box>
          <Box>
            <Typography variant={"h6"}>
              <strong>{headerTitle}</strong>
            </Typography>
            <Box mt={"2px"}>
              <Typography display={"inline"} style={{ verticalAlign: "bottom", marginRight: 8 }}>
                {portexId}
              </Typography>
              <StateStatus state={quoteRequest.state} big />
            </Box>
          </Box>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gridColumnGap={40}>
          <Box display="flex" flexDirection="column">
            <TextInfo label={t("common:mode")} heading={<LoadTypeChip mode={quoteRequest.mode} />} align={"right"} />
          </Box>
          <Box display="flex" flexDirection="column">
            <TextInfo
              label={t("shipper:equipmentType")}
              renderHeading={() => (
                <Box display={"flex"} alignItems={"center"} justifyContent="flex-end">
                  <Typography variant={"h6"} style={{ marginLeft: 8 }}>
                    <strong>{trailerType}</strong>
                  </Typography>
                </Box>
              )}
              align={"right"}
            />
          </Box>
          <Box display="flex" flexDirection="column">
            <TextInfo label={t("common:totalWeight")} heading={totalWeight} align={"right"} />
          </Box>
          <Box>
            <QuoteRequestHeaderActions quoteRequest={quoteRequest} refetchQuoteRequest={refetchQuoteRequest} />
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
};

LtlQuoteRequestHeader.fragments = {
  QuoteRequest: QUOTE_REQUEST,
};

export default LtlQuoteRequestHeader;
